/**
 * This device and web push (Holatlar 12): whether it can be asked, asking, and telling the server.
 * The server already sends (core.web_push) and stores the devices (`/api/me/push-subscriptions/`).
 */
import { apiRequest } from "@/lib/api";

const ASKED_KEY = "kapitan.push.asked";

export type PushState = "unsupported" | "default" | "granted" | "denied";

export function pushState(): PushState {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export function wasAsked(): boolean {
  try {
    return globalThis.localStorage.getItem(ASKED_KEY) !== null;
  } catch {
    return true;
  }
}

/** "Keyinroq": not asked again on this device; the profile keeps a way to turn it on. */
export function rememberAsked(): void {
  try {
    globalThis.localStorage.setItem(ASKED_KEY, "1");
  } catch {
    // Storage refused: the prompt returns next time, which costs one press.
  }
}

/** Blank when the deployment has no VAPID key: then nothing offers push at all. */
export async function pushPublicKey(): Promise<string> {
  const answer = await apiRequest<{ public_key: string }>("/api/me/push-subscriptions/public-key/");
  return answer.public_key;
}

/** The URL-safe base64 of a VAPID key, as `pushManager.subscribe` wants it. */
export function decodeKey(key: string): Uint8Array<ArrayBuffer> {
  const standard = key.replaceAll("-", "+").replaceAll("_", "/");
  const padded = standard.padEnd(Math.ceil(standard.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
}

/** Asks the browser, then registers this device. Answers with the permission it ended on. */
export async function enablePush(publicKey: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeKey(publicKey),
  });
  const { endpoint, keys } = subscription.toJSON();
  await apiRequest("/api/me/push-subscriptions/", {
    method: "POST",
    body: { endpoint, keys, user_agent: navigator.userAgent.slice(0, 255) },
  });
  return "granted";
}
